/*
  cart-free-candle.js
  Free candle gift-at-threshold logic for the ts-cart drawer.

  Loaded ONCE, from OUTSIDE the drawer's re-rendered .ts-cart__wrapper (see
  snippets/ts-cart-drawer.liquid), so it keeps running across every drawer
  re-render — and, critically, even when the cart starts empty. The drawer
  re-renders via `.innerHTML = ...` (assets/ts-cart-drawer.js renderContent),
  which never re-executes inline <script> tags, so the logic must live in a
  persistent asset rather than inside the re-rendered markup.

  Behaviour:
  - The toggle reflects eligibility only (cart subtotal EXCLUDING the gift line
    reaches the threshold). It is display-only, never toggled by the shopper.
  - The gift is NOT added to the drawer when the threshold is reached. It is
    added when the checkout button is clicked, and only if the threshold is met.
  - Any stale gift found in the drawer is removed so it never displays.

  Config comes from window.fcFreeCandleConfig, rendered in ts-cart-drawer.liquid.
*/
(function () {
  if (window.__fcFreeCandleInit) return;

  var CONFIG = window.fcFreeCandleConfig;
  if (!CONFIG || !CONFIG.variantId) return;

  window.__fcFreeCandleInit = true;

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var refreshing = false;
  var checkingOut = false;
  var checkTimer = null;

  // Count in-flight cart-mutating requests (quantity change, add, remove...) so
  // the checkout handler can wait for them to commit before reading the cart.
  // Without this, clicking checkout right after a quantity bump reads a stale
  // subtotal (the change POST hasn't committed yet) and the gift is skipped.
  var pendingMutations = 0;
  var idleResolvers = [];

  function whenCartIdle() {
    if (pendingMutations <= 0) return Promise.resolve();
    return new Promise(function (resolve) { idleResolvers.push(resolve); });
  }

  function settleIdle() {
    if (pendingMutations <= 0) {
      pendingMutations = 0;
      var resolvers = idleResolvers;
      idleResolvers = [];
      resolvers.forEach(function (resolve) { resolve(); });
    }
  }

  // Trailing debounce so the several triggers a single cart update fires (fetch
  // hook + renderContent wrap + mutation observer + theme cart event) collapse
  // into one check that runs AFTER the last one — i.e. after the drawer
  // re-render that recreates the promo markup.
  function scheduleCheck() {
    if (checkTimer) clearTimeout(checkTimer);
    checkTimer = setTimeout(function () {
      checkTimer = null;
      checkCart();
    }, 120);
  }

  function isGift(item) {
    if (item.properties && (item.properties._free_gift === 'true' || item.properties._free_gift === true)) {
      return true;
    }
    return item.variant_id === CONFIG.variantId;
  }

  // Sum the cart excluding the gift itself, and locate any stale gift line.
  function inspectCart(cart) {
    var giftLine = null;
    var otherTotal = 0;
    cart.items.forEach(function (item) {
      if (isGift(item)) {
        giftLine = item;
      } else {
        otherTotal += item.final_line_price;
      }
    });
    return {
      giftLine: giftLine,
      eligible: otherTotal >= CONFIG.threshold
    };
  }

  // Switch the toggle on/off based on eligibility. Display-only: the gift line is
  // never added here — it is only added when checkout is clicked.
  function applyToggle(eligible) {
    var banner = document.querySelector('.cart-free-candle');
    if (!banner) return;
    banner.classList.toggle('is-on', eligible);
    var toggle = banner.querySelector('.cart-free-candle__toggle');
    if (toggle) toggle.setAttribute('aria-checked', eligible ? 'true' : 'false');
  }

  function addGift() {
    return fetch(root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: [{ id: CONFIG.variantId, quantity: 1, properties: { _free_gift: 'true' } }]
      })
    });
  }

  function removeGift(key) {
    return fetch(root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 })
    });
  }

  function refreshDrawer() {
    var el = document.querySelector('ts-cart');
    if (!el || typeof el.renderContent !== 'function') return Promise.resolve();
    return fetch(window.location.pathname + '?sections=ts-cart-drawer')
      .then(function (res) { return res.json(); })
      .then(function (sections) { el.renderContent(sections); });
  }

  // Sync the toggle to eligibility and strip any stale gift so it never shows in
  // the drawer (e.g. after abandoning checkout with the gift already added). The
  // gift is only ever added at checkout click — see onCheckoutClick.
  function checkCart() {
    // Busy with a prior check — try again shortly so we never drop an update.
    if (refreshing) { scheduleCheck(); return; }
    refreshing = true;

    return fetch(root + 'cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var state = inspectCart(cart);
        applyToggle(state.eligible);
        if (state.giftLine) {
          return removeGift(state.giftLine.key).then(refreshDrawer);
        }
      })
      .catch(function () {})
      .finally(function () { refreshing = false; });
  }

  // Intercept the checkout button: add the gift (only when eligible), then
  // continue to the checkout URL. Delegated so it survives re-renders. The gift
  // is always (re)added last so it sits at the bottom of the cart: cart/add.js
  // appends new line items, so we remove any existing gift line first and add it
  // fresh rather than letting add.js bump an old line.
  function onCheckoutClick(e) {
    var btn = e.target.closest ? e.target.closest('.ts-cart__checkout') : null;
    if (!btn) return;
    if (checkingOut) return;

    e.preventDefault();
    checkingOut = true;
    var href = btn.getAttribute('href') || (root + 'checkout');

    // Wait for any in-flight cart mutation (e.g. a quantity change made just
    // before clicking) to commit, then read the cart uncached, so eligibility
    // reflects the true current subtotal. Cap the wait so a hung request can
    // never block checkout.
    var idleGuard = new Promise(function (resolve) { setTimeout(resolve, 3000); });
    Promise.race([whenCartIdle(), idleGuard])
      .then(function () { return fetch(root + 'cart.js', { cache: 'no-store' }); })
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        var state = inspectCart(cart);
        if (state.eligible) {
          // Re-add last: strip the old gift line (if any) then append fresh.
          if (state.giftLine) {
            return removeGift(state.giftLine.key).then(addGift);
          }
          return addGift();
        }
        // Not eligible — clean up any stale gift so it isn't checked out.
        if (state.giftLine) {
          return removeGift(state.giftLine.key);
        }
      })
      .catch(function () {})
      .then(function () { window.location.href = href; });
  }

  // Re-sync after every drawer re-render by wrapping renderContent once.
  function patchDrawer() {
    if (!window.customElements) return;
    window.customElements.whenDefined('ts-cart').then(function () {
      var proto = window.customElements.get('ts-cart').prototype;
      if (proto.__freeCandlePatched) return;
      proto.__freeCandlePatched = true;

      var original = proto.renderContent;
      proto.renderContent = function (sections) {
        var result = original.call(this, sections);
        Promise.resolve(result).then(function () {
          scheduleCheck();
        });
        return result;
      };
    });
  }

  // Catch-all: re-check after any cart-mutating request, no matter which script
  // (ts-cart, product form, theme.js Cart, or an app) triggered it.
  function patchFetch() {
    if (!window.fetch || window.fetch.__freeCandlePatched) return;
    var originalFetch = window.fetch;
    function patched(input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url);
      var result = originalFetch.apply(this, arguments);
      if (isCartMutation) {
        pendingMutations++;
        // Fires on both fulfilment and rejection so the counter never sticks.
        var done = function () {
          pendingMutations--;
          settleIdle();
          scheduleCheck();
        };
        result.then(done, done);
      }
      return result;
    }
    patched.__freeCandlePatched = true;
    window.fetch = patched;
  }

  // Belt-and-suspenders: observe the drawer wrapper so ANY re-render re-runs the
  // check, regardless of which script triggered it. The wrapper element persists
  // across renders (renderContent only swaps its innerHTML, replacing its direct
  // children) so a childList observer here fires on every drawer update.
  function observeDrawer() {
    if (!window.MutationObserver) return;
    var wrapper = document.querySelector('.ts-cart__wrapper');
    if (!wrapper) return;
    var observer = new MutationObserver(function () { scheduleCheck(); });
    observer.observe(wrapper, { childList: true });
  }

  function init() {
    patchFetch();
    patchDrawer();
    observeDrawer();
    document.addEventListener('click', onCheckoutClick);
    // The theme.js cart framework updates the cart without ts-cart's
    // renderContent — listen for its events too.
    window.addEventListener('theme:cart:updated', scheduleCheck);
    window.addEventListener('theme:cart:productAdded', scheduleCheck);
    // If the page is restored from the back/forward cache after a checkout
    // navigation, clear the guard (and re-sync) so checkout works again.
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        checkingOut = false;
        scheduleCheck();
      }
    });
    checkCart();
  }

  if (document.readyState !== 'loading') {
    init();
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
